/**
 * DISPLAY WORKSPACE — /google-ads/awareness
 *
 * Same look as the YouTube and Search workspaces (toolbar · stat tiles ·
 * filter bar · campaign → ad group → ad rows · list / grid), built for the
 * Display Network: ad groups show who and where they target, ads show their
 * real landscape image, and the grid previews each campaign's first ad.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { BarChart3, Copy, Image as ImageIcon, Info, Layers, LayoutTemplate, Megaphone, Pause, Play, Plus } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { AttachCustomerDialog } from '@/components/google-ads/AttachCustomerDialog';
import { ReuseAdDialog } from '@/components/google-ads/ReuseAdDialog';
import {
  DisplayAdRow, DisplayTargetingSummary, googleAdsService, GoogleAdsAdGroupRow, GoogleAdsCampaignSummary,
} from '@/services/google-ads.service';
import {
  adGroupAdResourceName, alreadyToasted, approvalOf, biddingLabel, errMsg, formatMicros, newestFirst, Pill, pretty, statusOf, Switch, useYoutubeAuth,
} from '@/components/google-ads/youtube/youtubeShared';
import { AGE_LABELS, GENDER_LABELS } from '@/pages/google-ads/googleAdsTargeting';
import { hostOf } from '@/components/google-ads/objectives/objectiveShared';
import {
  CampaignCard, CampaignFilters, ChannelTile, ChildRow, ChipLine, ConnectGate, EmptyWorkspace, Skeleton, StatTiles, useCampaignFilters,
  useCampaignToggle, useViewMode, WorkspaceToolbar,
} from '@/components/google-ads/objectives/workspaceChrome';
import { isDisplayCampaign } from './displayShared';

export interface DisplayWorkspaceConfig {
  title: string; base: string; subtitle: string; emptyHint: string;
}

type Counts = { byCampaign: Record<string, { adGroups: number; ads: number; assetGroups?: number }>; byAdGroup: Record<string, number> };

const firstImage = (ad: DisplayAdRow | null | undefined) =>
  ad ? (ad.marketingImages.find((i) => i.url)?.url ?? ad.squareImages.find((i) => i.url)?.url) : undefined;

// ── ads ──────────────────────────────────────────────────────────────────

function AdRows({ accountUid, customerId, adGroupId, parentPaused }: { accountUid: string; customerId: string; adGroupId: string; parentPaused: boolean }) {
  const [ads, setAds] = useState<DisplayAdRow[] | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const load = useCallback(async () => {
    setError('');
    try { setAds(await googleAdsService.display.listAds(accountUid, { adGroupId })); } catch (e) { setError(errMsg(e)); }
  }, [accountUid, adGroupId]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (ad: DisplayAdRow) => {
    setBusy(ad.adId);
    const rn = adGroupAdResourceName(customerId, ad.adGroupId, ad.adId);
    try {
      if (ad.status === 'ENABLED') await googleAdsService.pauseAd(accountUid, rn); else await googleAdsService.resumeAd(accountUid, rn);
      await load();
    } catch (e) { if (!alreadyToasted(e)) toast.error(`Google refused — ${errMsg(e)}`); } finally { setBusy(null); }
  };

  if (error) return <div className="py-2 pl-9 text-xs text-danger">{error} <button type="button" className="ml-1 underline" onClick={load}>Retry</button></div>;
  if (ads === null) return <Skeleton count={2} indent="pl-9" />;
  if (ads.length === 0) return <div className="py-3 pl-9 text-sm text-text-muted">No responsive display ads yet — the group can't show without one.</div>;
  return (
    <div className="pl-9">
      {ads.map((ad) => {
        const st = ad.status === 'ENABLED' && parentPaused ? { label: 'Parent paused', tone: 'paused' as const } : statusOf(ad.status);
        const approval = approvalOf(ad.approvalStatus, ad.reviewStatus);
        const img = firstImage(ad);
        const imgs = ad.marketingImages.length + ad.squareImages.length;
        const logos = ad.logos.length + ad.squareLogos.length;
        return (
          <div key={ad.adId} className="flex items-center gap-3.5 border-t border-border/40 py-3 first:border-t-0">
            <span className="flex h-10 w-[76px] shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-surface-alt text-text-muted">
              {img ? <img src={img} alt="" referrerPolicy="no-referrer" className="h-full w-full object-cover" /> : <ImageIcon size={14} />}
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-text-primary">{ad.headlines[0] || ad.longHeadline || ad.name || `Ad ${ad.adId}`}</div>
              <div className="mt-0.5 truncate text-xs text-text-muted">
                {[ad.businessName, ad.finalUrls[0] && hostOf(ad.finalUrls[0]), `${imgs} image${imgs === 1 ? '' : 's'}`, logos ? `${logos} logo${logos === 1 ? '' : 's'}` : null,
                  ad.youtubeVideoIds.length ? `${ad.youtubeVideoIds.length} video${ad.youtubeVideoIds.length === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ')}
              </div>
            </div>
            {ad.adStrength && !/UNSPECIFIED|UNKNOWN|PENDING|NO_ADS/.test(ad.adStrength) && (
              <span className="hidden rounded-full bg-surface-alt px-2 py-0.5 text-[10px] font-semibold text-text-secondary lg:inline">{pretty(ad.adStrength)}</span>
            )}
            {approval && <Pill {...approval} className="hidden sm:inline-flex" />}
            <Pill {...st} />
            <Switch checked={ad.status === 'ENABLED'} disabled={busy === ad.adId || ad.status === 'REMOVED'} label="Enable ad" onCheckedChange={() => toggle(ad)} />
          </div>
        );
      })}
    </div>
  );
}

// ── ad groups ────────────────────────────────────────────────────────────

function TargetingLines({ accountUid, adGroupId }: { accountUid: string; adGroupId: string }) {
  const [t, setT] = useState<DisplayTargetingSummary | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    googleAdsService.display.adGroupTargeting(accountUid, adGroupId).then((r) => { if (alive) setT(r); }).catch(() => { if (alive) setT(null); });
    return () => { alive = false; };
  }, [accountUid, adGroupId]);
  if (t === undefined) return <Skeleton count={1} indent="pl-9" h="h-6" />;
  if (t === null) return null;
  const audience = [
    t.ages ? t.ages.map((a) => AGE_LABELS[a] ?? a).join(', ') : 'All ages',
    t.genders ? t.genders.map((g) => GENDER_LABELS[g] ?? g).join(' / ') : 'All genders',
    t.optimizedTargeting === true ? 'Optimized targeting on' : t.optimizedTargeting === false ? 'Optimized targeting off' : null,
  ].filter((x): x is string => !!x);
  const content = [
    ...t.topics.map((x) => x.name), ...t.keywords.map((k) => `“${k}”`), ...t.placements,
    ...t.youtubeChannels.map((c) => `channel ${c.slice(0, 8)}…`), ...t.youtubeVideos.map((v) => `video ${v}`),
  ];
  return (
    <div className="space-y-1.5 py-2 pl-9">
      <ChipLine label="Audience" items={audience} />
      <ChipLine label="Content" items={content} empty="Whole Display Network — no topic, keyword or placement limits" />
    </div>
  );
}

function AdGroupRows({ accountUid, customerId, campaign, currency, base, adCounts, onChanged }: {
  accountUid: string; customerId: string; campaign: GoogleAdsCampaignSummary; currency: string | null; base: string;
  adCounts?: Record<string, number>; onChanged: () => void;
}) {
  const navigate = useNavigate();
  const [groups, setGroups] = useState<GoogleAdsAdGroupRow[] | null>(null);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [reuse, setReuse] = useState<GoogleAdsAdGroupRow | null>(null);
  const [revision, setRevision] = useState(0);
  const load = useCallback(async () => {
    setError('');
    try { setGroups((await googleAdsService.listAdGroups(accountUid, campaign.id)).filter((g) => !!g.id)); } catch (e) { setError(errMsg(e)); }
  }, [accountUid, campaign.id]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (g: GoogleAdsAdGroupRow) => {
    setBusy(g.id);
    try {
      if (g.status === 'ENABLED') await googleAdsService.pauseAdGroup(accountUid, g.id); else await googleAdsService.resumeAdGroup(accountUid, g.id);
      await load();
    } catch (e) { if (!alreadyToasted(e)) toast.error(`Google refused — ${errMsg(e)}`); } finally { setBusy(null); }
  };

  if (error) return <div className="py-2 text-xs text-danger">{error} <button type="button" className="ml-1 underline" onClick={load}>Retry</button></div>;
  if (groups === null) return <Skeleton count={2} indent="pl-8" />;
  const manualCpc = (campaign.biddingStrategyType ?? '').startsWith('MANUAL_CPC');
  return (
    <div className="space-y-1 pl-2">
      {reuse && <ReuseAdDialog accountUid={accountUid} group={reuse} onClose={() => setReuse(null)}
        onSaved={() => { setOpenId(reuse.id); setRevision((v) => v + 1); setReuse(null); onChanged(); }} />}
      {groups.length === 0 && <div className="py-2 text-sm text-text-muted">No ad groups yet — the campaign can't show without one.</div>}
      {groups.map((g) => {
        const open = openId === g.id;
        const st = statusOf(g.status, g.primaryStatus);
        const cpc = manualCpc ? formatMicros(g.cpcBidMicros, currency) : null;
        const n = adCounts?.[g.id];
        return (
          <ChildRow key={g.id} open={open} onToggle={() => setOpenId(open ? null : g.id)}
            lead={<Layers size={16} className="shrink-0 text-text-muted/70" />}
            title={g.name}
            sub={<>Display ad group{cpc ? ` · max CPC ${cpc}` : ''}{n !== undefined ? ` · ${n} ad${n === 1 ? '' : 's'}` : ''}</>}
            actions={g.status !== 'REMOVED' && (
              <>
                <Button variant="ghost" size="sm" className="hidden md:inline-flex" iconLeft={<Copy size={13} />} title="Copy a compatible ad into this group" onClick={() => setReuse(g)}>
                  Use existing ad
                </Button>
                <Button variant="ghost" size="sm" className="h-8 gap-1 px-2.5 text-sm text-text-secondary hover:text-text-primary" onClick={() => navigate(`${base}/create/ad?adgroup_id=${g.id}`)}>
                  <Plus size={13} /> Ad
                </Button>
              </>
            )}
            status={<><Pill {...st} /><Switch checked={g.status === 'ENABLED'} disabled={busy === g.id || g.status === 'REMOVED'} label={`Enable ${g.name}`} onCheckedChange={() => toggle(g)} /></>}>
            <TargetingLines accountUid={accountUid} adGroupId={g.id} />
            <AdRows key={`${g.id}-${revision}`} accountUid={accountUid} customerId={customerId} adGroupId={g.id}
              parentPaused={campaign.status !== 'ENABLED' || g.status !== 'ENABLED'} />
          </ChildRow>
        );
      })}
    </div>
  );
}

// ── workspace ────────────────────────────────────────────────────────────

export default function DisplayWorkspace({ cfg }: { cfg: DisplayWorkspaceConfig }) {
  const navigate = useNavigate();
  const { auth, account, accountUid, checkAuth } = useYoutubeAuth();
  const ready = !!(auth?.connected && auth?.customer_selected && accountUid);
  const customerId = account?.customerId ?? '';
  const currency = auth?.currency ?? null;
  const newRoute = `${cfg.base}/create/campaign`;

  const [campaigns, setCampaigns] = useState<GoogleAdsCampaignSummary[] | null>(null);
  const [counts, setCounts] = useState<Counts | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [view, setView] = useViewMode('gads_display_view');
  const [previews, setPreviews] = useState<Record<string, DisplayAdRow | null>>({});

  const load = useCallback(async () => {
    if (!ready) return;
    setListError(null);
    setRefreshing(true);
    try {
      setCampaigns(newestFirst((await googleAdsService.listCampaigns(accountUid)).filter(isDisplayCampaign)));
    } catch (e) {
      setListError(errMsg(e, 'Could not load campaigns.'));
      setCampaigns([]);
    } finally { setRefreshing(false); }
    googleAdsService.campaignCounts(accountUid).then(setCounts).catch(() => undefined);
  }, [ready, accountUid]);
  // customerId: re-attaching a different customer on the same account must refetch.
  useEffect(() => { load(); }, [load, customerId]);

  const f = useCampaignFilters(campaigns);
  const { busy, toggle } = useCampaignToggle(accountUid, load);

  // Grid cards show each campaign's first ad — one request for all of them.
  useEffect(() => {
    if (view !== 'grid' || f.live.length === 0 || !accountUid) return;
    let alive = true;
    googleAdsService.display.listAds(accountUid)
      .then((ads) => { if (alive) setPreviews(Object.fromEntries(f.live.map((c) => [c.id, ads.find((a) => a.campaignId === c.id && firstImage(a)) ?? ads.find((a) => a.campaignId === c.id) ?? null]))); })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [view, f.live, accountUid]);

  const subline = (c: GoogleAdsCampaignSummary) => {
    const cnt = counts?.byCampaign[c.id];
    const parts = [biddingLabel(c.biddingStrategyType), formatMicros(c.budgetAmountMicros, currency) && `${formatMicros(c.budgetAmountMicros, currency)} / day`].filter(Boolean);
    return (
      <>
        <span className="truncate">{parts.join(' · ') || 'Display'}</span>
        {cnt && (
          <span className="inline-flex items-center gap-2.5">
            <span className="inline-flex items-center gap-1"><Layers size={12} /> {cnt.adGroups} ad group{cnt.adGroups === 1 ? '' : 's'}</span>
            <span className="inline-flex items-center gap-1"><ImageIcon size={12} /> {cnt.ads} ad{cnt.ads === 1 ? '' : 's'}</span>
          </span>
        )}
      </>
    );
  };

  const enabledBudget = f.live.filter((c) => c.status === 'ENABLED').reduce((n, c) => n + Number(c.budgetAmountMicros || 0), 0);
  const stats = [
    { label: 'Campaigns', value: f.live.length, Icon: Megaphone },
    { label: 'Enabled', value: f.live.filter((c) => c.status === 'ENABLED').length, Icon: Play },
    { label: 'Paused', value: f.live.filter((c) => c.status === 'PAUSED').length, Icon: Pause },
    { label: 'Daily budget (enabled)', value: formatMicros(enabledBudget, currency) ?? '0', Icon: BarChart3 },
  ];

  return (
    <>
      <Topbar title={cfg.title} breadcrumb={`Google Ads / ${cfg.title}`} description={cfg.subtitle} />

      <div className="gads-workspace w-full px-5 py-4">
        {auth === undefined && <div className="space-y-3"><div className="skeleton h-14 rounded-2xl" /><Skeleton count={3} /></div>}
        {auth !== undefined && !ready && <ConnectGate auth={auth ?? { connected: false }} accountUid={accountUid} onChanged={checkAuth} />}

        {ready && (
          <div className="w-full pb-10">
            <WorkspaceToolbar auth={auth} onCustomer={() => setPickerOpen(true)} onRefresh={load} refreshing={refreshing}
              view={view} onView={setView} onNew={() => navigate(newRoute)} />
            <StatTiles stats={stats} loading={campaigns === null || !!listError} />
            <CampaignFilters f={f} />

            <div className="space-y-3">
              {listError && <Card role="alert" className="border-danger/30 bg-danger-soft p-4 text-sm text-danger">{listError}<button type="button" className="ml-3 underline" onClick={load}>Try again</button></Card>}
              {campaigns === null && !listError && <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="skeleton h-[68px] rounded-2xl" />)}</div>}

              {campaigns !== null && f.live.length === 0 && f.removed.length === 0 && !listError && (
                <EmptyWorkspace icon={LayoutTemplate} title="No Display campaigns yet" hint={cfg.emptyHint} onNew={() => navigate(newRoute)}
                  points={['Responsive display ads', 'Audience + content targeting', 'Viewable CPM or clicks', 'Starts paused']} />
              )}

              {view === 'list' && f.visible.length > 0 && (
                <div className="gads-campaign-list">
                  {f.visible.map((c) => (
                    <CampaignCard key={c.id} c={c} open={openId === c.id} onToggle={() => setOpenId(openId === c.id ? null : c.id)}
                      tile={<ChannelTile icon={LayoutTemplate} />} subline={subline(c)} busy={busy === c.id} onStatus={() => toggle(c)}>
                      <AdGroupRows accountUid={accountUid} customerId={customerId} campaign={c} currency={currency} base={cfg.base} adCounts={counts?.byAdGroup} onChanged={load} />
                      {c.status !== 'REMOVED' && (
                        <div className="pl-9 pt-3">
                          <Button variant="ghost" size="sm" className="h-8 gap-1.5 px-2.5 text-sm text-text-secondary hover:text-text-primary"
                            onClick={() => navigate(`${cfg.base}/create/adgroup?campaign_id=${c.id}`)}>
                            <Plus size={13} /> New ad group
                          </Button>
                        </div>
                      )}
                    </CampaignCard>
                  ))}
                </div>
              )}

              {view === 'grid' && f.visible.length > 0 && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 2xl:grid-cols-3">
                  {f.visible.map((c) => {
                    const st = statusOf(c.status, c.primaryStatus);
                    const ad = previews[c.id];
                    const img = firstImage(ad);
                    return (
                      <Card key={c.id} padded={false} className="flex flex-col overflow-hidden">
                        <div className="relative aspect-[1.91/1] w-full overflow-hidden bg-surface-alt">
                          {img
                            ? <img src={img} alt="" referrerPolicy="no-referrer" className="h-full w-full object-cover" />
                            : <div className="flex h-full items-center justify-center bg-primary-soft text-primary"><LayoutTemplate size={28} /></div>}
                          <span className="absolute right-2 top-2"><Pill {...st} /></span>
                          {ad && (ad.headlines[0] || ad.longHeadline) && (
                            <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/70 to-transparent px-3 pb-2 pt-6 text-xs font-medium text-white">
                              {ad.headlines[0] || ad.longHeadline}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-1 flex-col p-3.5">
                          <span className="truncate text-sm font-semibold text-text-primary">{c.name}</span>
                          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 truncate text-xs text-text-muted">{subline(c)}</div>
                          <div className="mt-3 flex items-center gap-2 border-t border-border/50 pt-3">
                            <Switch checked={c.status === 'ENABLED'} disabled={busy === c.id || c.status === 'REMOVED'} label={`Enable ${c.name}`} onCheckedChange={() => toggle(c)} />
                            <span className="text-xs text-text-muted">{c.status === 'ENABLED' ? 'Enabled' : c.status === 'REMOVED' ? 'Removed' : 'Paused'}</span>
                            <span className="flex-1" />
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => { setView('list'); setOpenId(c.id); }}>Open</Button>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}

              {campaigns !== null && (f.live.length > 0 || f.removed.length > 0) && f.visible.length === 0 && (
                <Card className="p-8 text-center text-sm text-text-muted">No campaigns match your filters.</Card>
              )}

              {campaigns !== null && !listError && f.live.length > 0 && (
                <p className="flex items-start gap-1.5 px-1 pt-1 text-2xs text-text-muted">
                  <Info size={12} className="mt-px shrink-0" />
                  <span>Every Display campaign on this Google Ads account is listed here — including ones built in Google Ads itself.</span>
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {ready && (
        <AttachCustomerDialog open={pickerOpen} accountUid={accountUid} onClose={() => setPickerOpen(false)}
          onAttached={async () => { setPickerOpen(false); await checkAuth(); }} />
      )}
    </>
  );
}

